import { useState, useEffect, useCallback } from "react";
import { FiSearch, FiArrowLeft, FiChevronRight, FiPrinter } from "react-icons/fi";
import { apiGet } from "../api";
import { PageHeader, Card, StatCard, Pill, Btn, THead, TD, Pagination, PER_PAGE_DEFAULT } from "./KioskUI";

const MMK = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtDate = (s) => new Date(s).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
const fmtDateLong = (s) => new Date(s).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
const fmtPoNo = (id) => `PO-${String(id).padStart(5, "0")}`;

/* ─── Print restock invoice ─── */
export function printRestockInvoice(inv) {
  const rows = inv.items.map(item => `
    <tr>
      <td>${item.product_name}<span class="sku">${item.sku || ""}</span></td>
      <td class="r">${item.quantity}</td>
      <td class="r">${item.unit_price != null ? MMK(item.unit_price) : "—"}</td>
      <td class="r">${item.total != null ? MMK(item.total) : "—"}</td>
    </tr>`).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${fmtPoNo(inv.restock_id)}</title>
  <style>
    *{margin:0;padding:0;box-sizing:border-box;font-family:-apple-system,Segoe UI,Roboto,sans-serif}
    body{padding:40px 46px;color:#131820}
    .top{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #131820;padding-bottom:18px}
    .biz{font-size:22px;font-weight:800;letter-spacing:-.5px}.sub{font-size:12px;color:#697583;margin-top:3px}
    .inv{text-align:right}.inv .n{font-size:18px;font-weight:800;color:#1D4ED8}.inv .d{font-size:12px;color:#697583;margin-top:4px}
    .meta{display:flex;gap:48px;margin:22px 0 18px}
    .meta .l{font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#94A0AE;font-weight:700}
    .meta .v{font-size:14px;font-weight:600;margin-top:4px}
    table{width:100%;border-collapse:collapse;margin-top:6px}
    th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#94A0AE;padding:9px 10px;border-bottom:1px solid #DCE2EA}
    th.r,td.r{text-align:right}
    td{padding:11px 10px;border-bottom:1px solid #EAEEF3;font-size:13.5px}
    td .sku{display:block;font-size:11px;color:#94A0AE;margin-top:2px}
    .totals{margin-left:auto;width:280px;margin-top:18px}
    .totals .row{display:flex;justify-content:space-between;font-size:13.5px;padding:5px 0;color:#4A5563}
    .totals .grand{border-top:2px solid #131820;margin-top:6px;padding-top:10px;font-size:18px;font-weight:800;color:#131820}
    .foot{margin-top:40px;font-size:11.5px;color:#94A0AE;text-align:center;border-top:1px solid #EAEEF3;padding-top:16px}
  </style></head><body>
  <div class="top">
    <div><div class="biz">Hein Khant Zaw</div><div class="sub">Clothing Wholesale · Yangon</div></div>
    <div class="inv"><div class="n">${fmtPoNo(inv.restock_id)}</div><div class="d">${fmtDateLong(inv.date)}</div></div>
  </div>
  <div class="meta">
    <div><div class="l">Purchased from</div><div class="v">${inv.supplier.name}</div></div>
    <div><div class="l">Contact</div><div class="v">${inv.supplier.phone || inv.supplier.contact || "—"}</div></div>
    <div><div class="l">Received by</div><div class="v">${inv.employee}</div></div>
  </div>
  <table><thead><tr><th>Product</th><th class="r">Qty</th><th class="r">Unit cost (MMK)</th><th class="r">Amount</th></tr></thead>
  <tbody>${rows}</tbody></table>
  <div class="totals">
    <div class="row"><span>Goods cost</span><span>${MMK(inv.total_cost)} Ks</span></div>
    ${inv.transportation_fee > 0 ? `<div class="row"><span>Transportation</span><span>+ ${MMK(inv.transportation_fee)} Ks</span></div>` : ""}
    <div class="row grand"><span>Total</span><span>${MMK(inv.total_amount)} MMK</span></div>
  </div>
  <div class="foot">${fmtPoNo(inv.restock_id)}</div>
  <div class="no-print" style="margin-top:32px;text-align:center">
    <button onclick="window.close()" style="padding:10px 28px;font-size:14px;border:1px solid #ddd;border-radius:8px;background:#f4f6f9;cursor:pointer">✕ Close without printing</button>
  </div>
  <style>@media print{.no-print{display:none}}</style>
  </body></html>`;

  const w = window.open("", "_blank", "width=720,height=900");
  if (!w) { alert("Please allow pop-ups to print the invoice."); return; }
  w.document.open(); w.document.write(html); w.document.close();
  w.focus();
  setTimeout(() => {
    try { w.print(); } catch (e) {}
    w.onafterprint = () => w.close();
  }, 350);
}

/* ─── Restock invoice detail ─── */
export function RestockInvoiceDetail({ restockId, token, onBack }) {
  const [inv, setInv]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    apiGet(`/api/inventory/restocks/${restockId}/invoice`, token)
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setInv(d); })
      .catch(() => setError("Cannot load invoice."))
      .finally(() => setLoading(false));
  }, [restockId, token]);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240 }}>
      <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
    </div>
  );

  if (error || !inv) return (
    <div style={{ textAlign: "center", padding: "48px 0", color: "var(--danger)" }}>{error || "Invoice not found."}</div>
  );

  return (
    <div style={{ maxWidth: 880, margin: "0 auto" }}>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
        <button onClick={onBack} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 8, cursor: "pointer", display: "flex" }}>
          <FiArrowLeft size={18} color="var(--sub)" />
        </button>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 11, minWidth: 0 }}>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 25, fontWeight: 800, letterSpacing: "-.025em", margin: 0, color: "var(--ink)", whiteSpace: "nowrap" }}>{fmtPoNo(inv.restock_id)}</h1>
          <Pill tone="info">Purchase</Pill>
        </div>
        <Btn kind="ghost" onClick={() => printRestockInvoice(inv)} style={{ flexShrink: 0 }}>
          <FiPrinter size={16} /> Print
        </Btn>
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        {/* business header */}
        <div style={{ padding: "22px 26px", borderBottom: "1px solid var(--hair)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24 }}>
          <div>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 19, fontWeight: 800, color: "var(--ink)", letterSpacing: "-.02em" }}>Hein Khant Zaw</div>
            <div style={{ fontSize: 12.5, color: "var(--sub)", marginTop: 3 }}>Clothing Wholesale · Yangon</div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 800, color: "var(--primary)" }}>{fmtPoNo(inv.restock_id)}</div>
            <div style={{ fontSize: 12.5, color: "var(--sub)", marginTop: 3 }}>{fmtDateLong(inv.date)}</div>
          </div>
        </div>

        {/* meta row */}
        <div style={{ padding: "18px 26px", borderBottom: "1px solid var(--hair)", display: "flex", gap: 48, flexWrap: "wrap" }}>
          <MetaField label="Purchased from" value={inv.supplier?.name || "Unknown supplier"} />
          <MetaField label="Contact" value={inv.supplier?.phone || inv.supplier?.contact || "—"} />
          <MetaField label="Received by" value={inv.employee} />
        </div>

        {/* line items */}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <THead cols={[{ t: "Product" }, { t: "SKU" }, { t: "Qty", r: 1 }, { t: "Unit cost", r: 1 }, { t: "Amount", r: 1 }]} />
          <tbody>
            {inv.items.map((item, i) => (
              <tr key={i}>
                <TD b>{item.product_name}</TD>
                <TD mut>{item.sku || "—"}</TD>
                <TD r>{item.quantity}</TD>
                <TD r mut>{item.unit_price != null ? `${MMK(item.unit_price)} Ks` : "—"}</TD>
                <TD r b>{item.total != null ? `${MMK(item.total)} Ks` : "—"}</TD>
              </tr>
            ))}
          </tbody>
        </table>

        {/* totals */}
        <div style={{ padding: "18px 26px", background: "var(--faint)", display: "flex", justifyContent: "flex-end" }}>
          <div style={{ width: 300 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, color: "var(--sub)", padding: "5px 0" }}>
              <span>Goods cost</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{MMK(inv.total_cost)} Ks</span>
            </div>
            {inv.transportation_fee > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13.5, color: "var(--sub)", padding: "5px 0" }}>
                <span>Transportation</span>
                <span style={{ fontVariantNumeric: "tabular-nums" }}>+ {MMK(inv.transportation_fee)} Ks</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 6, paddingTop: 11, borderTop: "2px solid var(--ink)" }}>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 800 }}>Total</span>
              <span style={{ fontFamily: "var(--font-head)", fontSize: 23, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                {MMK(inv.total_amount)} <span style={{ fontSize: 12.5, color: "var(--sub)" }}>MMK</span>
              </span>
            </div>
          </div>
        </div>
      </Card>
      {inv.note && (
        <div style={{ marginTop: 16, padding: "12px 16px", background: "var(--faint)", borderRadius: "var(--r-md)", fontSize: 13, color: "var(--sub)" }}>
          Note: {inv.note}
        </div>
      )}
    </div>
  );
}

function MetaField({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--n-400)" }}>{label}</div>
      <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--ink)", marginTop: 4 }}>{value}</div>
    </div>
  );
}

/* ─── Restock invoice list ─── */
export default function RestockInvoices({ token, go }) {
  const [restocks, setRestocks] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [q, setQ]               = useState("");
  const [page, setPage]         = useState(1);
  const [view, setView]         = useState("list");
  const [selectedId, setSelectedId] = useState(null);

  const fetchRestocks = useCallback(async () => {
    try {
      const r = await apiGet("/api/inventory/restocks", token);
      const d = await r.json();
      setRestocks(Array.isArray(d) ? d : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchRestocks(); }, [fetchRestocks]);

  if (view === "detail") {
    return (
      <RestockInvoiceDetail
        restockId={selectedId}
        token={token}
        onBack={() => { setView("list"); setSelectedId(null); }}
      />
    );
  }

  const grand = restocks.reduce((s, r) => s + Number(r.total_amount), 0);
  const transport = restocks.reduce((s, r) => s + Number(r.transportation_fee), 0);

  const qq = q.trim().toLowerCase();
  const filtered = restocks.filter(r =>
    !qq || fmtPoNo(r.restock_id).toLowerCase().includes(qq) || (r.supplier_name || "").toLowerCase().includes(qq)
  );
  const rows = filtered.slice((page - 1) * PER_PAGE_DEFAULT, page * PER_PAGE_DEFAULT);

  return (
    <div>
      <PageHeader title="Restock Invoices" mm="ဝယ်ယူမှု ဘောက်ချာ" desc={`${restocks.length} restock${restocks.length !== 1 ? "s" : ""} · every purchase on record`}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "9px 13px", width: 280 }}>
          <FiSearch size={16} color="var(--n-400)" style={{ flexShrink: 0 }} />
          <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="Search PO # or supplier…"
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 14, color: "var(--ink)", width: "100%" }} />
        </div>
        <Btn kind="acc" icon="restock" onClick={() => go("restock")}>Receive Stock</Btn>
      </PageHeader>

      {/* stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16, marginBottom: 18 }}>
        <StatCard label="Total Purchased" value={(grand / 1e6).toFixed(2) + "M"} unit="MMK" icon="invoice" tone="blue" sub={MMK(grand) + " Ks"} />
        <StatCard label="Restocks" value={restocks.length} icon="restock" tone="blue" sub="purchase orders" />
        <StatCard label="Transportation" value={(transport / 1e6).toFixed(2) + "M"} unit="MMK" icon="payments" tone="amber" sub={MMK(transport) + " Ks total"} />
      </div>

      {/* table */}
      <Card style={{ overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "48px 20px", textAlign: "center", color: "var(--sub)", fontSize: 14 }}>Loading…</div>
        ) : (
          <>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <THead cols={[
              { t: "PO #" }, { t: "Date" }, { t: "Supplier" },
              { t: "Items", r: 1 }, { t: "Received by" },
              { t: "Total", r: 1 }, { t: "", r: 1, w: 40 },
            ]} />
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} style={{ padding: "32px 16px", textAlign: "center", color: "var(--n-400)", fontSize: 14 }}>
                  {qq ? "No restocks match." : "No restocks yet."}
                </td></tr>
              )}
              {rows.map(r => (
                <tr key={r.restock_id} style={{ cursor: "pointer" }}
                  onClick={() => { setSelectedId(r.restock_id); setView("detail"); }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <TD b>{fmtPoNo(r.restock_id)}</TD>
                  <TD mut>{fmtDate(r.date)}</TD>
                  <TD>{r.supplier_name || "—"}</TD>
                  <TD r mut>{r.items_count}</TD>
                  <TD mut>{r.employee_name || "—"}</TD>
                  <TD r b>{MMK(r.total_amount)}</TD>
                  <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)", textAlign: "right" }}>
                    <FiChevronRight size={17} color="var(--n-300)" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination total={filtered.length} page={page} onChange={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}
